import React from 'react';
import { Card, CardContent, Grid, Typography, Box, IconButton, Tooltip, Popover } from '@mui/material';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import NumberFormatTextField from '../../../../../components/NumberFormatTextField/NumberFormatTextField';
import MySlider from '../../../../../components/MySlider/MySlider';
import PolicyInterestCalculatorModal from '../PolicyInterestCalculatorModal/PolicyInterestCalculatorModal';
import { getTargetPolicyInterest } from '../calculator';
import CustomInputSelect from '../../../../Analysis/partials/CustomInputSelect/CustomInputSelect';
import CampaignCodeControl from '../../../../../components/CampaignCodeControl/CampaignCodeControl';
import { tracking } from '../../../../../utils/tracking';
import {
  getEffectiveReturnRealizationRate,
  normalizeReturnRealizationRate,
  RETURN_REALIZATION_MAX,
  RETURN_REALIZATION_MIN,
  RETURN_REALIZATION_STEP
} from '../return-realization-rate';
import {
  buildLinkedIllustration,
  getIllustrationBreakdownAtYear,
  getIllustrationCashValueAtYear,
  getLinkedIllustrationLabel,
  hasIllustrationYear,
  refreshCashValuesFromLinkedIllustration
} from '../../../../../utils/linked-illustration';
import { ILLUSTRATION_FEATURE_PREMIUM_FINANCING, toIllustrationLinkedId } from '../../../IllustrationOcr/utils';
import {
  peekIllustrationUpload,
  restoreIllustrationUpload,
  subscribeIllustrationUpload,
  takeIllustrationUpload
} from '../../../../../utils/native/incoming-share-handoff';

const YEAR_OPTIONS = Array.from(Array(20).keys()).map(e => e + 1);

/**
 * Apply a selected illustration OCR record onto the current pFin profile.
 */
export const applyIllustrationToProfile = ({
  record,
  yearsToShow,
  cashValueAt,
  handleChange,
  overrides = {},
  returnRealizationRate = 100,
  returnRealizationRateEnabled = false
}) => {
  if (!record || !handleChange) return;

  const totalPremium = overrides.principal != null ? overrides.principal : record.meta?.premium?.totalBasicPremium;
  const linkedIllustration = buildLinkedIllustration(record);
  const rate = getEffectiveReturnRealizationRate(returnRealizationRateEnabled, returnRealizationRate);

  let nextCashValueAt = null;
  if (yearsToShow && cashValueAt) {
    const refreshed = refreshCashValuesFromLinkedIllustration(yearsToShow, cashValueAt, linkedIllustration, rate);
    if (refreshed) {
      nextCashValueAt = refreshed;
    } else {
      const seeded = [...cashValueAt];
      let changed = false;
      yearsToShow.forEach((year, index) => {
        if (index === 0 || year == null) return;
        const cashValue = getIllustrationCashValueAtYear(linkedIllustration, year, rate);
        if (cashValue != null) {
          seeded[index] = cashValue;
          changed = true;
        }
      });
      if (changed) nextCashValueAt = seeded;
    }
  }

  if (
    totalPremium == null &&
    !nextCashValueAt &&
    !linkedIllustration &&
    overrides.loanPercentage == null &&
    overrides.firstYearBonus == null &&
    overrides.base == null
  ) {
    return;
  }

  handleChange({
    target: {
      name: 'importIllustration',
      value: {
        principal: totalPremium,
        cashValueAt: nextCashValueAt,
        loanPercentage: overrides.loanPercentage,
        firstYearBonus: overrides.firstYearBonus,
        base: overrides.base,
        linkedIllustration,
        // Keep realization-rate off on first link;
        returnRealizationRateEnabled: false,
        returnRealizationRate: normalizeReturnRealizationRate(returnRealizationRate),
        // 首日退保價值 — only when OCR found an explicit day-0 figure
        ...(typeof record.meta?.initialSurrenderValue === 'number' && !Number.isNaN(record.meta.initialSurrenderValue)
          ? { day0SurrenderValue: record.meta.initialSurrenderValue }
          : {})
      }
    }
  });
};

export default class InputPanel extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      calculatorOpen: false,
      importDialogOpen: false,
      importInitialFile: null,
      importInitialPassword: '',
      // Lazily resolved — a static import of ImportIllustrationDialog pulls
      // illustration/duck → api → fetch → store/index while the duck's
      // initialState is still in TDZ, which crashes the premium-financing PDF
      // bundle with "Cannot access 'c' before initialization".
      ImportIllustrationDialog: null,
      breakdownAnchorEl: null,
      breakdownIndex: null
    };

    this.illustrationConsumer = null;
  }

  hydrateIllustrationConsumer = (profileId, linkedIllustration) => {
    const illustrationId = toIllustrationLinkedId(linkedIllustration?._id);
    const linkedId = toIllustrationLinkedId(profileId);
    if (illustrationId && linkedId) {
      this.illustrationConsumer = { illustrationId, linkedId };
    }
  };

  componentDidMount() {
    this.illustrationAlive = true;
    this.hydrateIllustrationConsumer(this.props.profileId, this.props.linkedIllustration);
    this.unsubscribeIllustrationUpload = subscribeIllustrationUpload(() => this.consumeIllustrationUpload());
    this.consumeIllustrationUpload();
  }

  componentWillUnmount() {
    this.illustrationAlive = false;
    if (this.unsubscribeIllustrationUpload) this.unsubscribeIllustrationUpload();
    // Profiles can reload and unmount this panel while the shared document is
    // still opening. Put it back so the next panel continues into OCR.
    if (this.openedUpload && this.state.importDialogOpen) {
      restoreIllustrationUpload(this.openedUpload);
    }
  }

  consumeIllustrationUpload = () => {
    if (this.props.template || this.state.importDialogOpen || this.openingIllustration) return;
    const upload = peekIllustrationUpload();
    if (!upload) return;
    this.openingIllustration = true;
    this.openImportDialogWithFile(upload);
  };

  openImportDialogWithFile = upload => {
    const next = {
      importDialogOpen: true,
      importInitialFile: upload.file,
      importInitialPassword: upload.password || ''
    };
    if (this.state.ImportIllustrationDialog) {
      this.setState(next);
      return;
    }
    import('../../../IllustrationOcr')
      .then(mod => {
        if (!this.illustrationAlive) return;
        this.setState({
          ImportIllustrationDialog: mod.ImportIllustrationDialog,
          ...next
        });
      })
      .catch(() => {
        this.openingIllustration = false;
      });
  };

  componentDidUpdate(prevProps, prevState) {
    if (
      prevProps.profileId !== this.props.profileId ||
      toIllustrationLinkedId(prevProps.linkedIllustration?._id) !==
        toIllustrationLinkedId(this.props.linkedIllustration?._id)
    ) {
      if (prevProps.profileId !== this.props.profileId) {
        this.illustrationConsumer = null;
      }
      this.hydrateIllustrationConsumer(this.props.profileId, this.props.linkedIllustration);
    }
    if (!prevState.importDialogOpen && this.state.importDialogOpen && this.state.importInitialFile) {
      const upload = peekIllustrationUpload();
      if (upload && upload.file === this.state.importInitialFile) {
        this.openedUpload = takeIllustrationUpload();
        this.openingIllustration = false;
      }
    }
    if (prevProps.isCurrencyEnabled && !this.props.isCurrencyEnabled) {
      this.consumeIllustrationUpload();
    }
  }

  closeImportDialog = () => {
    this.openedUpload = null;
    this.openingIllustration = false;
    this.setState({ importDialogOpen: false, importInitialFile: null, importInitialPassword: '' }, () =>
      this.consumeIllustrationUpload()
    );
  };

  openImportDialog = () => {
    if (this.props.template) return;
    if (this.state.ImportIllustrationDialog) {
      this.setState({ importDialogOpen: true });
      return;
    }
    import('../../../IllustrationOcr').then(mod => {
      this.setState({
        ImportIllustrationDialog: mod.ImportIllustrationDialog,
        importDialogOpen: true
      });
    });
  };

  handleOpenBreakdown = (event, index) => {
    this.setState({ breakdownAnchorEl: event.currentTarget, breakdownIndex: index });
  };

  handleCloseBreakdown = () => {
    this.setState({ breakdownAnchorEl: null, breakdownIndex: null });
  };

  renderBreakdownContent = index => {
    const { intl, yearsToShow, linkedIllustration, returnRealizationRate, returnRealizationRateEnabled } = this.props;
    if (index == null || !yearsToShow) return null;
    const year = yearsToShow[index];
    const rate = returnRealizationRate == null ? 100 : returnRealizationRate;
    const effectiveRate = getEffectiveReturnRealizationRate(returnRealizationRateEnabled, rate);
    const breakdown = getIllustrationBreakdownAtYear(linkedIllustration, year, effectiveRate);
    if (!breakdown) return null;

    const formatCurrency = value =>
      value == null ? '-' : `$${Number(value).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
    const rows = [
      { id: 'pfa-cash-value-guaranteed', value: breakdown.guaranteed },
      { id: 'pfa-cash-value-non-guaranteed', value: breakdown.nonGuaranteed },
      { id: 'pfa-cash-value-expected-surrender', value: breakdown.expectedSurrenderValue }
    ];

    return (
      <Box sx={{ py: 0.5 }}>
        <Typography sx={{ fontSize: 12, fontWeight: 700, mb: 0.5 }}>
          {intl.formatMessage({ id: 'pfa-cash-value-breakdown-title' })}
        </Typography>
        {rows.map(row => (
          <Box key={row.id} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
            <Typography sx={{ fontSize: 12 }}>{intl.formatMessage({ id: row.id })}</Typography>
            <Typography sx={{ fontSize: 12, fontWeight: 600 }}>{formatCurrency(row.value)}</Typography>
          </Box>
        ))}
      </Box>
    );
  };

  handleYearsToShow = (event, index) => {
    const newYears = [...this.props.yearsToShow];
    newYears[index] = event.target.value || null;
    this.props.handleChange({
      target: {
        name: 'yearsToShow',
        value: newYears
      }
    });
  };

  handleInputCashValueAt = (event, index) => {
    const { cashValueAt } = this.props;
    const newCashValue = [...cashValueAt];
    newCashValue[index] = event.target.value;
    this.props.handleChange({
      target: {
        name: 'cashValueAt',
        value: newCashValue
      }
    });
  };

  /** Snapshot value on focus so we only unlink after a real edit, not a no-op blur. */
  handleCashValueFocus = index => {
    this.cashValueEditSnapshot = {
      index,
      value: this.props.cashValueAt?.[index]
    };
  };

  /** Manual cash-value edit breaks the illustration link (kept values, drops auto-refresh). */
  handleCashValueBlur = index => {
    const { linkedIllustration, cashValueAt } = this.props;
    if (!linkedIllustration) return;
    const snapshot = this.cashValueEditSnapshot;
    if (!snapshot || snapshot.index !== index) return;
    if (cashValueAt?.[index] !== snapshot.value) {
      this.handleUnlinkIllustration();
    }
    this.cashValueEditSnapshot = null;
  };

  handleReturnRealizationRate = (event, value) => {
    this.props.handleChange({
      target: {
        name: 'returnRealizationRate',
        value: normalizeReturnRealizationRate(value)
      }
    });
  };

  handleReturnRealizationRateInput = event => {
    this.handleReturnRealizationRate(event, event.target.value);
  };

  handleSelectIllustration = (record, overrides) => {
    const { yearsToShow, cashValueAt, handleChange, returnRealizationRate, returnRealizationRateEnabled, profileId } =
      this.props;
    const linkedId = toIllustrationLinkedId(profileId);
    const nextIllustrationId = toIllustrationLinkedId(record?._id);
    if (nextIllustrationId && linkedId) {
      this.illustrationConsumer = { illustrationId: nextIllustrationId, linkedId };
    }
    // Keep this local until the pFin version is saved. illustrations.meta.linked
    // is written on POST /pFin-calculator, not on import/select.
    applyIllustrationToProfile({
      record,
      yearsToShow,
      cashValueAt,
      handleChange,
      overrides,
      returnRealizationRate,
      returnRealizationRateEnabled
    });
  };

  /** Detach the linked illustration and turn off realization-rate controls. */
  handleUnlinkIllustration = () => {
    this.illustrationConsumer = null;
    this.props.handleChange({
      target: {
        name: 'importIllustration',
        value: { linkedIllustration: null, returnRealizationRateEnabled: false }
      }
    });
  };

  render() {
    const {
      intl,
      principal,
      term,
      handleChange,
      yearsToShow,
      cashValueAt,
      returnRealizationRate,
      returnRealizationRateEnabled = false,
      isCurrencyEnabled,
      template,
      linkedIllustration
    } = this.props;
    const {
      calculatorOpen,
      importDialogOpen,
      importInitialFile,
      importInitialPassword,
      ImportIllustrationDialog,
      breakdownAnchorEl,
      breakdownIndex
    } = this.state;
    const rate = normalizeReturnRealizationRate(returnRealizationRate);

    return (
      <Card className={`pfa-card pf-card-container pf-side-input-panel${template ? ' template' : ''}`}>
        <CardContent style={{ padding: template ? '16px 24px 8px 36px' : 24 }}>
          {!template && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 0.5
              }}
            >
              <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                {intl.formatMessage({ id: 'pfa-policy-value' })}
              </Typography>
              <CampaignCodeControl targetCampaignCode={['FEATURE_PFINOCR']}>
                <Tooltip title={intl.formatMessage({ id: 'pfa-import-illustration' })}>
                  <span>
                    <IconButton
                      color="primary"
                      size="small"
                      disabled={isCurrencyEnabled}
                      aria-label={intl.formatMessage({ id: 'pfa-import-illustration' })}
                      onClick={this.openImportDialog}
                      sx={{ p: 0.5 }}
                    >
                      <PhotoCameraIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </CampaignCodeControl>
            </Box>
          )}
          {template && (
            <Typography sx={{ fontSize: 14, fontWeight: 400, mb: 0.5 }}>
              {intl.formatMessage({ id: 'pfa-policy-value' })}
            </Typography>
          )}

          {!template && linkedIllustration && (
            <Box sx={{ mb: 1 }}>
              <Typography noWrap sx={{ fontSize: 12, color: 'text.secondary', minWidth: 0 }}>
                {intl.formatMessage({ id: 'pfa-illustration-linked' })}
                {getLinkedIllustrationLabel(linkedIllustration)
                  ? `: ${getLinkedIllustrationLabel(linkedIllustration)}`
                  : ''}
              </Typography>
            </Box>
          )}

          <Box className="pfa-term-cash-value-table">
            <Grid container size={12} spacing={1} sx={{ flexDirection: 'column' }}>
              <Grid
                container
                style={{ marginBottom: 12 }}
                sx={{
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <Grid size={5}>
                  <Box style={{ borderBottom: '3px solid grey', marginRight: '2px' }}>
                    <Typography
                      className="input-control MuiInputLabel-root form-label"
                      style={{
                        marginBottom: '8px',
                        fontSize: 14,
                        fontWeight: template ? 400 : 600,
                        textAlign: 'center'
                      }}
                    >
                      {intl.formatMessage({ id: 'pfa-term' })}
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={7}>
                  <Box style={{ borderBottom: '3px solid rgb(102, 94, 255)' }}>
                    <Typography
                      className="input-control cash-value-at MuiInputLabel-root form-label"
                      style={{
                        marginBottom: '8px',
                        fontSize: 14,
                        fontWeight: template ? 400 : 600,
                        textAlign: 'center'
                      }}
                    >
                      {intl.formatMessage({ id: 'pfa-cash-value' })}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
              {yearsToShow.map((y, i) => {
                if (i === 0) {
                  return null;
                }

                const year = yearsToShow[i];
                const handleChangeCashValue = isCurrencyEnabled
                  ? undefined
                  : event => this.handleInputCashValueAt(event, i);

                const effectiveRate = getEffectiveReturnRealizationRate(returnRealizationRateEnabled, rate);
                const hasBreakdown = Boolean(getIllustrationBreakdownAtYear(linkedIllustration, year, effectiveRate));
                // Warn instead of silently leaving the value untouched when the linked
                // illustration has no row for this policy year.
                const yearMissing =
                  Boolean(linkedIllustration) && year != null && !hasIllustrationYear(linkedIllustration, year);

                const cashValueField = (
                  <NumberFormatTextField
                    disabled={isCurrencyEnabled}
                    name="cashValueAt"
                    value={cashValueAt[i]}
                    onChange={handleChangeCashValue}
                    onFocus={isCurrencyEnabled ? undefined : () => this.handleCashValueFocus(i)}
                    onBlur={isCurrencyEnabled ? undefined : () => this.handleCashValueBlur(i)}
                    inputProps={{
                      allowNegative: false,
                      prefix: '$',
                      decimalScale: 0,
                      isAllowed: () => true
                    }}
                  />
                );

                return (
                  <Grid
                    container
                    size={12}
                    key={i}
                    className="pfa-term-cash-value-row"
                    sx={{
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <Grid size={5} className="input-control">
                      <CustomInputSelect
                        disabled={isCurrencyEnabled}
                        onChange={e => this.handleYearsToShow(e, i)}
                        value={year || ''}
                        selectOptions={YEAR_OPTIONS}
                        endAdornmentLabel={intl.formatMessage({ id: 'Yrs' })}
                        isDollarValue={true}
                        allowCustomValue={true}
                        min={1}
                        max={99}
                      />
                    </Grid>
                    <Grid size={7} className="input-control cash-value-at">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>{cashValueField}</Box>
                        {hasBreakdown && (
                          <IconButton
                            size="small"
                            color="primary"
                            aria-label={intl.formatMessage({ id: 'pfa-cash-value-breakdown-title' })}
                            onClick={event => this.handleOpenBreakdown(event, i)}
                            sx={{ p: 0.25, flexShrink: 0 }}
                          >
                            <InfoOutlinedIcon fontSize="small" />
                          </IconButton>
                        )}
                        {yearMissing && (
                          <Tooltip title={intl.formatMessage({ id: 'pfa-illustration-year-missing' })}>
                            <WarningAmberIcon
                              color="warning"
                              fontSize="small"
                              aria-label={intl.formatMessage({ id: 'pfa-illustration-year-missing' })}
                              sx={{ flexShrink: 0 }}
                            />
                          </Tooltip>
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                );
              })}
            </Grid>
          </Box>

          {!template && linkedIllustration && returnRealizationRateEnabled && (
            <Grid size={12} className="slider-control" sx={{ mt: 2 }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 1,
                  gap: 1
                }}
              >
                <Typography color="textSecondary" sx={{ minWidth: 0 }}>
                  {intl.formatMessage({ id: 'pfa-return-realization-rate' })}
                </Typography>
                <Box sx={{ width: 72, flexShrink: 0 }}>
                  <NumberFormatTextField
                    disabled={isCurrencyEnabled}
                    name="returnRealizationRate"
                    value={rate}
                    onChange={this.handleReturnRealizationRateInput}
                    inputProps={{
                      allowNegative: false,
                      suffix: '%',
                      decimalScale: 0,
                      isAllowed: values => {
                        const { formattedValue, floatValue } = values;
                        return (
                          formattedValue === '' ||
                          (floatValue >= RETURN_REALIZATION_MIN && floatValue <= RETURN_REALIZATION_MAX)
                        );
                      }
                    }}
                  />
                </Box>
              </Box>
              <div className="labelled-slider">
                <MySlider
                  disabled={isCurrencyEnabled}
                  min={RETURN_REALIZATION_MIN}
                  max={RETURN_REALIZATION_MAX}
                  step={RETURN_REALIZATION_STEP}
                  value={rate}
                  onChange={this.handleReturnRealizationRate}
                  onChangeCommitted={this.handleReturnRealizationRate}
                />
              </div>
            </Grid>
          )}
        </CardContent>
        <Popover
          open={Boolean(breakdownAnchorEl)}
          anchorEl={breakdownAnchorEl}
          onClose={this.handleCloseBreakdown}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          slotProps={{ paper: { sx: { px: 1.5, py: 0.5, minWidth: 180 } } }}
        >
          {this.renderBreakdownContent(breakdownIndex)}
        </Popover>
        {!template && ImportIllustrationDialog && (
          <ImportIllustrationDialog
            open={importDialogOpen}
            onClose={this.closeImportDialog}
            onSelect={this.handleSelectIllustration}
            feature={ILLUSTRATION_FEATURE_PREMIUM_FINANCING}
            initialFile={importInitialFile}
            initialPdfPassword={importInitialPassword}
          />
        )}
        <PolicyInterestCalculatorModal
          intl={intl}
          open={calculatorOpen}
          principal={principal}
          term={term}
          onClose={() => {
            this.setState({ calculatorOpen: false });
          }}
          onSubmitTargetPolicyValue={targetPolicyValue => {
            handleChange({
              target: {
                name: 'policyInterest',
                value: getTargetPolicyInterest({ principal, targetPolicyValue, term })
              }
            });
            this.setState({ calculatorOpen: false });
          }}
        />
      </Card>
    );
  }
}
